/**
 * A local symbol catalogue.
 *
 * TradingView's own symbol search lives inside the iframe and can't be reached
 * from here, so search runs against this list instead. Keeping it local means
 * no API key, no rate limit, and instant results — the trade-off is coverage,
 * which is fine for a curated workspace.
 */
export const SYMBOLS = [
  // Mega-cap tech
  { symbol: "NASDAQ:AAPL", name: "Apple", group: "Tech" },
  { symbol: "NASDAQ:MSFT", name: "Microsoft", group: "Tech" },
  { symbol: "NASDAQ:GOOGL", name: "Alphabet", group: "Tech" },
  { symbol: "NASDAQ:AMZN", name: "Amazon", group: "Tech" },
  { symbol: "NASDAQ:META", name: "Meta Platforms", group: "Tech" },
  { symbol: "NASDAQ:NVDA", name: "NVIDIA", group: "Tech" },
  { symbol: "NASDAQ:TSLA", name: "Tesla", group: "Tech" },
  { symbol: "NASDAQ:NFLX", name: "Netflix", group: "Tech" },
  { symbol: "NASDAQ:AMD", name: "AMD", group: "Tech" },
  { symbol: "NASDAQ:INTC", name: "Intel", group: "Tech" },
  { symbol: "NASDAQ:AVGO", name: "Broadcom", group: "Tech" },
  { symbol: "NYSE:CRM", name: "Salesforce", group: "Tech" },
  { symbol: "NYSE:ORCL", name: "Oracle", group: "Tech" },
  { symbol: "NYSE:IBM", name: "IBM", group: "Tech" },
  { symbol: "NASDAQ:ADBE", name: "Adobe", group: "Tech" },
  { symbol: "NASDAQ:PLTR", name: "Palantir", group: "Tech" },
  { symbol: "NASDAQ:PYPL", name: "PayPal", group: "Tech" },
  { symbol: "NASDAQ:SHOP", name: "Shopify", group: "Tech" },
  { symbol: "NASDAQ:UBER", name: "Uber", group: "Tech" },

  // Financials & industrials
  { symbol: "NYSE:JPM", name: "JPMorgan Chase", group: "Financials" },
  { symbol: "NYSE:BAC", name: "Bank of America", group: "Financials" },
  { symbol: "NYSE:GS", name: "Goldman Sachs", group: "Financials" },
  { symbol: "NYSE:V", name: "Visa", group: "Financials" },
  { symbol: "NYSE:MA", name: "Mastercard", group: "Financials" },
  { symbol: "NYSE:BRK.B", name: "Berkshire Hathaway", group: "Financials" },
  { symbol: "NYSE:DIS", name: "Disney", group: "Consumer" },
  { symbol: "NYSE:NKE", name: "Nike", group: "Consumer" },
  { symbol: "NYSE:KO", name: "Coca-Cola", group: "Consumer" },
  { symbol: "NASDAQ:SBUX", name: "Starbucks", group: "Consumer" },
  { symbol: "NASDAQ:COST", name: "Costco", group: "Consumer" },
  { symbol: "NYSE:WMT", name: "Walmart", group: "Consumer" },
  { symbol: "NYSE:BA", name: "Boeing", group: "Industrials" },
  { symbol: "NYSE:CAT", name: "Caterpillar", group: "Industrials" },
  { symbol: "NYSE:XOM", name: "Exxon Mobil", group: "Energy" },
  { symbol: "NYSE:CVX", name: "Chevron", group: "Energy" },

  // Indices & ETFs
  { symbol: "AMEX:SPY", name: "S&P 500 ETF", group: "Indices" },
  { symbol: "NASDAQ:QQQ", name: "Nasdaq 100 ETF", group: "Indices" },
  { symbol: "AMEX:DIA", name: "Dow Jones ETF", group: "Indices" },
  { symbol: "AMEX:IWM", name: "Russell 2000 ETF", group: "Indices" },
  { symbol: "AMEX:VTI", name: "Total Market ETF", group: "Indices" },
  { symbol: "TVC:VIX", name: "Volatility Index", group: "Indices" },

  // Crypto
  { symbol: "COINBASE:BTCUSD", name: "Bitcoin", group: "Crypto" },
  { symbol: "COINBASE:ETHUSD", name: "Ethereum", group: "Crypto" },
  { symbol: "COINBASE:SOLUSD", name: "Solana", group: "Crypto" },
  { symbol: "COINBASE:XRPUSD", name: "XRP", group: "Crypto" },
  { symbol: "COINBASE:DOGEUSD", name: "Dogecoin", group: "Crypto" },
  { symbol: "COINBASE:LTCUSD", name: "Litecoin", group: "Crypto" },
  { symbol: "COINBASE:AVAXUSD", name: "Avalanche", group: "Crypto" },

  // FX & commodities
  { symbol: "FX:EURUSD", name: "Euro / US Dollar", group: "Forex" },
  { symbol: "FX:GBPUSD", name: "Pound / US Dollar", group: "Forex" },
  { symbol: "FX:USDJPY", name: "US Dollar / Yen", group: "Forex" },
  { symbol: "FX:AUDUSD", name: "Aussie / US Dollar", group: "Forex" },
  { symbol: "TVC:GOLD", name: "Gold", group: "Commodities" },
  { symbol: "TVC:SILVER", name: "Silver", group: "Commodities" },
  { symbol: "TVC:USOIL", name: "Crude Oil", group: "Commodities" },
];

/** Bare ticker without the exchange prefix — "NASDAQ:AAPL" → "AAPL". */
export const tickerOf = (symbol) => String(symbol).split(":").pop();

/**
 * Ranked search over ticker and company name.
 *
 * Prefix matches outrank substring matches so typing "A" surfaces AAPL and
 * AMD before Bank of America.
 */
export const searchSymbols = (query, limit = 8) => {
  const q = query.trim().toLowerCase();
  if (!q) return SYMBOLS.slice(0, limit);

  const scored = [];

  for (const entry of SYMBOLS) {
    const ticker = tickerOf(entry.symbol).toLowerCase();
    const name = entry.name.toLowerCase();

    let score = -1;
    if (ticker === q) score = 0;
    else if (ticker.startsWith(q)) score = 1;
    else if (name.startsWith(q)) score = 2;
    else if (ticker.includes(q)) score = 3;
    else if (name.includes(q)) score = 4;

    if (score >= 0) scored.push({ entry, score });
  }

  return scored
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map((item) => item.entry);
};

export const findSymbol = (symbol) =>
  SYMBOLS.find((entry) => entry.symbol === symbol);
